import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, setDoc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';

// Firestore path for the per-user recording metadata doc that the transcode
// Cloud Function writes to. Exported so sharing code can also update it
// (e.g. pushing recipient UIDs into `viewerUids`).
export const mediaRecordingDocPath = (userId, recordingId) =>
  `users/${userId}/mediaRecordings/${recordingId}`;

// Maximum per-recording duration caps (client-side guardrails).
// Uploads exceeding these will also be rejected by storage rules.
export const MAX_VIDEO_DURATION_MS = 15 * 60 * 1000; // 15 minutes
export const MAX_AUDIO_DURATION_MS = 60 * 60 * 1000; // 60 minutes

// Re-encodes an AudioBuffer as a 16-bit PCM WAV Blob (RIFF/WAVE).
// Used after iPad audio recordings are decoded so the upload is in a
// container every browser can play AND every browser can run through
// decodeAudioData (which is required for WaveSurfer's peak generation).
function audioBufferToWavBlob(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const numSamples = audioBuffer.length;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numSamples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  let offset = 0;
  const writeStr = (s) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  };

  // RIFF header
  writeStr('RIFF');
  view.setUint32(offset, 36 + dataSize, true); offset += 4;
  writeStr('WAVE');
  // fmt chunk
  writeStr('fmt ');
  view.setUint32(offset, 16, true); offset += 4;        // PCM fmt chunk size
  view.setUint16(offset, 1, true); offset += 2;         // audioFormat = PCM
  view.setUint16(offset, numChannels, true); offset += 2;
  view.setUint32(offset, sampleRate, true); offset += 4;
  view.setUint32(offset, byteRate, true); offset += 4;
  view.setUint16(offset, blockAlign, true); offset += 2;
  view.setUint16(offset, bitsPerSample, true); offset += 2;
  // data chunk
  writeStr('data');
  view.setUint32(offset, dataSize, true); offset += 4;

  // Interleave channels and convert float [-1, 1] → int16
  const channels = [];
  for (let c = 0; c < numChannels; c++) channels.push(audioBuffer.getChannelData(c));
  for (let i = 0; i < numSamples; i++) {
    for (let c = 0; c < numChannels; c++) {
      let sample = Math.max(-1, Math.min(1, channels[c][i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

// Decodes a recorded blob (e.g. iPad's fragmented MP4) using the local
// browser's AudioContext, then re-encodes as WAV. Safari can decode its own
// fMP4 output, so this runs successfully on the iPad where the recording was
// made — and the resulting WAV plays/decodes everywhere else (avoiding
// freeze/crash when other browsers' decodeAudioData hits fragmented MP4).
async function transcodeBlobToWav(blob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) throw new Error('AudioContext not available');
  const ctx = new AudioCtx();
  try {
    const arrayBuffer = await blob.arrayBuffer();
    // decodeAudioData on Safari accepts a callback form on older versions,
    // so wrap to support both promise- and callback-based signatures.
    const audioBuffer = await new Promise((resolve, reject) => {
      try {
        const maybePromise = ctx.decodeAudioData(arrayBuffer, resolve, reject);
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise.then(resolve, reject);
        }
      } catch (err) {
        reject(err);
      }
    });
    return audioBufferToWavBlob(audioBuffer);
  } finally {
    try { await ctx.close(); } catch (_) { /* ignore */ }
  }
}

export class RecordingService {
  constructor() {
    this.mediaRecorder = null;
    this.mediaStream = null;
    this.rawStream = null; // Unmodified stream from getUserMedia (for live preview)
    this.recordedChunks = [];
    this.isRecording = false;
    this.gainNode = null;
    this.audioContext = null;
    this.mode = 'audio'; // 'audio' | 'video'
    this.activeMimeType = null;
    this.startTimestamp = null;
    this.maxDurationMs = MAX_AUDIO_DURATION_MS;
    this.maxDurationTimer = null;
    this.onMaxDurationReached = null;
  }

  async startRecording(options = {}) {
    try {
      console.log('Starting recording with options:', options);

      const {
        mode = 'audio',
        recordingMode = 'music',
        qualitySettings = null,
        deviceId = null,
        gainSettings = null,
        onMaxDurationReached = null
      } = options;

      this.mode = mode === 'video' ? 'video' : 'audio';
      this.onMaxDurationReached = typeof onMaxDurationReached === 'function'
        ? onMaxDurationReached
        : null;
      this.maxDurationMs = this.mode === 'video'
        ? MAX_VIDEO_DURATION_MS
        : MAX_AUDIO_DURATION_MS;

      // Default quality settings
      const defaultQuality = {
        sampleRate: 48000,
        channels: 2,
        bitrate: 320000
      };

      const quality = qualitySettings || defaultQuality;
      
      // Different constraints based on use case
      const musicConstraints = {
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          // Disable processing that interferes with musical content
          echoCancellation: false,  // Important for music!
          noiseSuppression: false,  // Can remove musical nuances
          autoGainControl: false,   // Can cause volume fluctuations
          
          // High quality settings from quality selector
          sampleRate: quality.sampleRate,
          channelCount: quality.channels,
          latency: 0,               // Minimize latency
          
          // Advanced constraints for better quality
          googEchoCancellation: false,
          googAutoGainControl: false,
          googNoiseSuppression: false,
          googHighpassFilter: false,
          googAudioMirroring: false
        }
      };

      const speechConstraints = {
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          // Keep processing for speech clarity
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: quality.sampleRate,
          channelCount: Math.min(quality.channels, 1) // Force mono for speech
        }
      };

      let constraints;
      if (this.mode === 'video') {
        constraints = {
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          // For video, allow the browser's default processing since speech is
          // typical for teaching scenarios; callers can override by passing a
          // full qualitySettings object with audio-only constraints.
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: quality.sampleRate
          }
        };
        console.log('Using video constraints:', constraints);
      } else {
        constraints = recordingMode === 'music' ? musicConstraints : speechConstraints;
        console.log('Using audio constraints:', constraints);
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (this.mode === 'video') {
        console.log('Got video stream with tracks:',
          stream.getTracks().map(t => ({ kind: t.kind, label: t.label })));
      } else {
        console.log('Got original audio stream with tracks:',
          stream.getAudioTracks().map(t => t.getSettings()));
      }

      this.recordedChunks = [];
      this.rawStream = stream;
      this.mediaStream = stream; // Default to original stream
      
      // Apply gain control if specified (audio-only; video mode bypasses the
      // AudioContext chain because we need to preserve the video track order).
      if (this.mode !== 'video' && gainSettings && gainSettings.gain !== undefined && gainSettings.gain !== 1.0) {
        console.log(`Attempting to apply gain: ${gainSettings.gain}x`);
        try {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
          
          // Resume audio context if needed
          if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
          }
          
          const source = this.audioContext.createMediaStreamSource(stream);
          this.gainNode = this.audioContext.createGain();
          
          // Set gain value with logging
          this.gainNode.gain.value = gainSettings.gain;
          console.log(`Gain node created with value: ${this.gainNode.gain.value}`);
          
          // Create destination for the gained stream
          const destination = this.audioContext.createMediaStreamDestination();
          
          // Connect: source -> gain -> destination
          source.connect(this.gainNode);
          this.gainNode.connect(destination);
          
          // Use the gained stream for recording
          this.mediaStream = destination.stream;
          console.log(`Successfully applied gain control. New stream tracks:`, 
                     this.mediaStream.getAudioTracks().map(t => t.getSettings()));
          
          // Add monitoring to verify gain is working
          const analyser = this.audioContext.createAnalyser();
          this.gainNode.connect(analyser);
          
          // Optional: Log audio levels periodically during recording
          const monitorLevels = () => {
            if (this.isRecording && analyser) {
              const dataArray = new Uint8Array(analyser.frequencyBinCount);
              analyser.getByteFrequencyData(dataArray);
              const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
              console.log(`Recording level with ${gainSettings.gain}x gain:`, average);
            }
          };
          
          // Monitor levels every 2 seconds during recording
          this.levelMonitorInterval = setInterval(monitorLevels, 2000);
          
        } catch (error) {
          console.error('Failed to apply gain control:', error);
          console.warn('Falling back to original stream without gain control');
          // Clean up any partial setup
          if (this.audioContext) {
            this.audioContext.close().catch(console.warn);
            this.audioContext = null;
          }
          this.gainNode = null;
          // mediaStream is already set to original stream above
        }
      } else {
        console.log('No gain adjustment needed, using original stream');
      }
      
      // Configure MediaRecorder with quality settings
      const bestMime = this.getBestMimeType();
      this.activeMimeType = bestMime;
      const recorderOptions = this.mode === 'video'
        ? {
            mimeType: bestMime,
            videoBitsPerSecond: 2_500_000,
            audioBitsPerSecond: 128_000
          }
        : {
            mimeType: bestMime,
            audioBitsPerSecond: quality.bitrate,
            audioBitrateMode: 'constant'
          };

      console.log('Creating MediaRecorder with options:', recorderOptions);
      this.mediaRecorder = new MediaRecorder(this.mediaStream, recorderOptions);
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
          console.log(`Recorded chunk: ${event.data.size} bytes`);
        }
      };

      this.mediaRecorder.onstart = () => {
        console.log('MediaRecorder started');
      };

      this.mediaRecorder.onstop = () => {
        console.log('MediaRecorder stopped');
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.startTimestamp = Date.now();

      // Enforce duration cap: auto-stop if the user blows past the limit so a
      // runaway video recorder can't blow up storage quotas.
      if (this.maxDurationTimer) {
        clearTimeout(this.maxDurationTimer);
      }
      this.maxDurationTimer = setTimeout(() => {
        if (this.isRecording) {
          console.warn('Max recording duration reached; auto-stopping.');
          if (this.onMaxDurationReached) {
            try { this.onMaxDurationReached(); } catch (e) { /* noop */ }
          }
        }
      }, this.maxDurationMs);

      console.log('Recording started successfully');
      return true;
    } catch (error) {
      console.error('Error starting recording:', error);
      // Ensure any partially-acquired tracks are released so the camera light
      // doesn't linger after a failed start.
      this._forceReleaseStreams();
      throw error;
    }
  }

  // Expose the live, unmodified stream for UI preview (e.g. video element).
  getLiveStream() {
    return this.rawStream || this.mediaStream || null;
  }

  // Streaming recording — chunks upload during capture so the blob-at-stop
  // is tiny. The returned StreamingUploader is owned by the service so
  // getLiveStream() can still show a preview. Caller awaits `uploader.stop()`
  // to get the finalized recording metadata.
  async startStreamingRecording(options = {}) {
    const {
      mode = 'audio',
      recordingMode = 'music',
      qualitySettings = null,
      deviceId = null,
      userId,
      recordingId,
      itemId,
      sessionId,
      ownerType = 'session',
      onStatus = () => {},
      onMaxDurationReached = null,
      timesliceMs
    } = options;

    if (!userId) throw new Error('startStreamingRecording requires userId');

    this.mode = mode === 'video' ? 'video' : 'audio';
    this.onMaxDurationReached = typeof onMaxDurationReached === 'function'
      ? onMaxDurationReached
      : null;
    this.maxDurationMs = this.mode === 'video' ? MAX_VIDEO_DURATION_MS : MAX_AUDIO_DURATION_MS;

    const defaultQuality = { sampleRate: 48000, channels: 2, bitrate: 320000 };
    const quality = qualitySettings || defaultQuality;

    // Same constraints shape as the legacy startRecording path — we reuse the
    // rules so music-mode audio retains its disabled AGC/NS/EC processing.
    const musicConstraints = {
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        sampleRate: quality.sampleRate,
        channelCount: quality.channels,
        latency: 0,
        googEchoCancellation: false,
        googAutoGainControl: false,
        googNoiseSuppression: false,
        googHighpassFilter: false,
        googAudioMirroring: false
      }
    };
    const speechConstraints = {
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: quality.sampleRate,
        channelCount: Math.min(quality.channels, 1)
      }
    };

    let constraints;
    if (this.mode === 'video') {
      constraints = {
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: quality.sampleRate
        }
      };
    } else {
      constraints = recordingMode === 'music' ? musicConstraints : speechConstraints;
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    this.rawStream = stream;
    this.mediaStream = stream;

    // Pick a mime type. getBestMimeType reads this.mode (set above).
    const mimeType = this.getBestMimeType();
    this.activeMimeType = mimeType;

    const { StreamingUploader } = await import('./streamingUploader');
    const uploader = new StreamingUploader({
      userId,
      recordingId: recordingId || `recording_${Date.now()}`,
      mediaType: this.mode,
      itemId,
      sessionId,
      stream,
      mimeType,
      ownerType,
      videoBitsPerSecond: 2_500_000,
      audioBitsPerSecond: this.mode === 'video' ? 128_000 : quality.bitrate,
      timesliceMs,
      onStatus,
      onMaxDurationReached: this.onMaxDurationReached
    });

    this.isRecording = true;
    this.startTimestamp = Date.now();
    this.activeStreamingUploader = uploader;

    try {
      await uploader.start();
    } catch (err) {
      this.isRecording = false;
      this._forceReleaseStreams();
      this.activeStreamingUploader = null;
      throw err;
    }
    return uploader;
  }

  async stopStreamingRecording() {
    const uploader = this.activeStreamingUploader;
    if (!uploader) return null;
    this.activeStreamingUploader = null;
    this.isRecording = false;
    try {
      const meta = await uploader.stop();
      // stop() already releases the capture tracks.
      this.rawStream = null;
      this.mediaStream = null;
      return meta;
    } catch (err) {
      console.error('stopStreamingRecording failed:', err);
      this.rawStream = null;
      this.mediaStream = null;
      throw err;
    }
  }

  async cancelStreamingRecording(reason) {
    const uploader = this.activeStreamingUploader;
    if (!uploader) return;
    this.activeStreamingUploader = null;
    this.isRecording = false;
    try {
      await uploader.cancel(reason);
    } finally {
      this.rawStream = null;
      this.mediaStream = null;
    }
  }

  _forceReleaseStreams() {
    try {
      if (this.rawStream) {
        this.rawStream.getTracks().forEach(t => { try { t.stop(); } catch (e) { /* noop */ } });
        this.rawStream = null;
      }
    } catch (e) { /* noop */ }
    try {
      if (this.mediaStream && this.mediaStream !== this.rawStream) {
        this.mediaStream.getTracks().forEach(t => { try { t.stop(); } catch (e) { /* noop */ } });
      }
    } catch (e) { /* noop */ }
    this.mediaStream = null;
  }

  stopRecording() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        console.log('No active recording to stop');
        resolve(null);
        return;
      }

      console.log('Stopping recording...');

      this.mediaRecorder.onstop = async () => {
        console.log('Recording stopped, cleaning up...');

        // Clear max-duration guard timer
        if (this.maxDurationTimer) {
          clearTimeout(this.maxDurationTimer);
          this.maxDurationTimer = null;
        }

        // Clear level monitoring
        if (this.levelMonitorInterval) {
          clearInterval(this.levelMonitorInterval);
          this.levelMonitorInterval = null;
        }
        
        // Clean up audio context and gain node
        if (this.gainNode) {
          console.log('Disconnecting gain node');
          try {
            this.gainNode.disconnect();
          } catch (e) {
            console.warn('Error disconnecting gain node:', e);
          }
          this.gainNode = null;
        }
        
        if (this.audioContext && this.audioContext.state !== 'closed') {
          console.log('Closing audio context');
          this.audioContext.close().then(() => {
            console.log('Audio context closed');
          }).catch(e => {
            console.warn('Error closing audio context:', e);
          });
          this.audioContext = null;
        }
        
        // Clean up media stream (and raw stream if separate)
        const stopTracksOn = (s) => {
          if (!s) return;
          s.getTracks().forEach(track => {
            try { track.stop(); } catch (e) { /* noop */ }
            console.log(`Stopped track: ${track.kind}, ${track.label}`);
          });
        };
        stopTracksOn(this.mediaStream);
        if (this.rawStream && this.rawStream !== this.mediaStream) {
          stopTracksOn(this.rawStream);
        }
        this.mediaStream = null;
        this.rawStream = null;

        // Create blob from recorded chunks
        if (this.recordedChunks.length > 0) {
          const blobType = this.activeMimeType || this.getBestMimeType();
          let blob = new Blob(this.recordedChunks, { type: blobType });
          console.log(`Created recording blob: ${blob.size} bytes, type: ${blob.type}`);

          // iPad/Safari MediaRecorder produces fragmented MP4 — Safari can
          // play and decode it, but other browsers' decodeAudioData can't,
          // which freezes / OOM-crashes the tab when WaveSurfer tries to
          // compute peaks. For audio-only recordings, re-encode here as WAV
          // (using Safari's own decoder while we still have it) so the
          // uploaded file is universally decodable. We deliberately skip
          // video uploads — those go through the iOS direct-upload fallback
          // and are played back via <video>, not WaveSurfer.
          if (this.mode === 'audio' && /^audio\/mp4/i.test(blob.type)) {
            try {
              console.log(`Transcoding fMP4 → WAV (input ${blob.size} bytes)`);
              const wavBlob = await transcodeBlobToWav(blob);
              console.log(`Transcode complete: ${wavBlob.size} bytes WAV`);
              blob = wavBlob;
              this.activeMimeType = 'audio/wav';
            } catch (transcodeError) {
              console.error('WAV transcode failed; uploading original mp4:', transcodeError);
              // Fall through with the original mp4 blob — better than
              // losing the recording entirely.
            }
          }

          resolve(blob);
        } else {
          console.warn('No recorded chunks available');
          resolve(null);
        }
      };

      this.mediaRecorder.stop();
      this.isRecording = false;
    });
  }

  // Store recording locally (in component state)
  saveRecordingLocally(blob, practiceItemId, sessionId, extras = {}) {
    const timestamp = Date.now();
    const recordingId = `recording_${timestamp}`;
    const mediaType = extras.mediaType || this.mode || 'audio';

    // Create object URL for local playback. The `audioUrl` field name is kept
    // for backward compatibility with existing renderers; for video we also
    // populate `previewUrl` so the ReviewView can mount a <video> element.
    const localUrl = URL.createObjectURL(blob);

    const duration = this.startTimestamp
      ? Math.round((Date.now() - this.startTimestamp) / 1000)
      : 0;

    const recordingMetadata = {
      id: recordingId,
      practiceItemId,
      sessionId,
      blob: blob,
      audioUrl: localUrl, // legacy field, used by audio players
      previewUrl: localUrl, // new field, generic playback URL for local preview
      mediaType,
      status: 'uploading', // will transition via Cloud Function transcode
      timestamp: timestamp,
      size: blob.size,
      duration,
      comments: [],
      regions: [],
      createdAt: new Date(),
      isLocal: true // Flag to indicate this is not yet uploaded
    };

    console.log('Recording saved locally:', recordingMetadata);
    return recordingMetadata;
  }

  // Upload recording to Firebase (called when session is finalized).
  //
  // Uses the *legacy* public-read path
  //   `recordings/{userId}/{sessionId}/{itemId}_{ts}.{ext}`
  // rather than the new `raw/{mediaType}/…` path. Two reasons:
  //
  //   1. Instructors need to fetch shared audio recordings via their own
  //      Firebase auth (different uid), and the `raw/{mediaType}/…` rule is
  //      owner-only read. The legacy rule allows public read, which is fine
  //      because Firebase download URLs are tokenised — only holders of the
  //      URL (distributed via Firestore share docs that already have their
  //      own access control) can fetch the file.
  //   2. This path is NOT watched by `transcodeMedia`, which is correct:
  //      `uploadRecording` is only ever called for legacy single-blob
  //      uploads (audio everywhere, video on iOS/WebKit). Streaming video
  //      still lands at `raw/video/…` via the assembly CF.
  async uploadRecording(recordingMetadata, userId, uploadExtras = {}) {
    try {
      console.log('Uploading recording to Firebase:', recordingMetadata.id);

      const mediaType = recordingMetadata.mediaType || 'audio';
      const ownerType = uploadExtras.ownerType || 'session';
      const ownerId = uploadExtras.ownerId || recordingMetadata.sessionId;
      const viewerUids = Array.isArray(uploadExtras.viewerUids) ? uploadExtras.viewerUids : [];

      const safeTs = recordingMetadata.timestamp || Date.now();
      const safeItem = recordingMetadata.practiceItemId || 'item';
      const safeSession = recordingMetadata.sessionId || 'session';
      // Pick an extension that matches the actual container. iOS would
      // normally emit MP4, but for audio we transcode to WAV in
      // stopRecording() so that decodeAudioData works on every browser.
      // Everything else emits WebM.
      const blobType = recordingMetadata.blob?.type || '';
      const ext = blobType.includes('wav')
        ? 'wav'
        : blobType.includes('mp4')
          ? 'mp4'
          : 'webm';
      const filename = `recordings/${userId}/${safeSession}/${safeItem}_${safeTs}.${ext}`;
      const storageRef = ref(storage, filename);

      const snapshot = await uploadBytes(storageRef, recordingMetadata.blob, {
        contentType: blobType || (mediaType === 'video' ? 'video/webm' : 'audio/webm'),
        customMetadata: {
          mediaType,
          ownerType,
          ownerId: String(ownerId),
          userId: String(userId),
          recordingId: String(recordingMetadata.id || ''),
          viewerUids: viewerUids.join(',')
        }
      });
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Clean up local object URL
      if (recordingMetadata.audioUrl && recordingMetadata.audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(recordingMetadata.audioUrl);
      }
      if (recordingMetadata.previewUrl && recordingMetadata.previewUrl.startsWith('blob:') && recordingMetadata.previewUrl !== recordingMetadata.audioUrl) {
        URL.revokeObjectURL(recordingMetadata.previewUrl);
      }

      // Direct-upload path (no transcode pipeline). Works for both audio and
      // video — video is the iOS fallback where the raw MP4 is directly
      // playable via the downloadURL. Status is 'ready' because no CF work
      // is pending.
      return {
        ...recordingMetadata,
        mediaType,
        url: downloadURL,
        filename: filename,
        storagePath: filename,
        audioUrl: mediaType === 'video' ? null : downloadURL,
        previewUrl: downloadURL,
        status: 'ready',
        isLocal: false,
        blob: undefined
      };
    } catch (error) {
      console.error('Error uploading recording:', error);
      throw error;
    }
  }

  // Save all recordings metadata to Firestore (called after upload)
  async saveSessionRecordings(sessionId, userId, recordings) {
    try {
      console.log('Saving session recordings to Firestore');

      const sessionRef = doc(db, 'users', userId, 'sessions', sessionId);
      
      // Organize recordings by practice item
      const recordingsByItem = {};
      recordings.forEach(recording => {
        if (!recordingsByItem[recording.practiceItemId]) {
          recordingsByItem[recording.practiceItemId] = [];
        }
        recordingsByItem[recording.practiceItemId].push({
          id: recording.id,
          url: recording.url,
          filename: recording.filename,
          timestamp: recording.timestamp,
          size: recording.size,
          duration: recording.duration,
          comments: recording.comments,
          regions: recording.regions,
          createdAt: recording.createdAt
        });
      });

      // Check if session document exists
      const sessionDoc = await getDoc(sessionRef);
      if (!sessionDoc.exists()) {
        await setDoc(sessionRef, {
          sessionId: sessionId,
          userId: userId,
          createdAt: new Date(),
          recordings: recordingsByItem
        });
      } else {
        await updateDoc(sessionRef, {
          recordings: recordingsByItem
        });
      }

      console.log('Session recordings saved successfully');
      return true;
    } catch (error) {
      console.error('Error saving session recordings:', error);
      throw error;
    }
  }

  // Delete local recording
  deleteLocalRecording(recordingMetadata) {
    try {
      if (recordingMetadata.audioUrl && recordingMetadata.isLocal) {
        // Only revoke object URLs that we created
        URL.revokeObjectURL(recordingMetadata.audioUrl);
      }
      console.log('Local recording deleted:', recordingMetadata.id);
    } catch (error) {
      console.warn('Error cleaning up recording URL:', error);
    }
  }

  // Save audio analysis data (comments, regions, markers)
  async saveAudioData(recordingId, practiceItemId, sessionId, userId, audioData) {
    try {
      console.log('RecordingService: Saving audio data for recording:', recordingId);
      console.log('RecordingService: Audio data to save:', {
        recordingId,
        practiceItemId,
        sessionId,
        userId,
        regions: audioData.regions || [],
        comments: audioData.comments || [],
        markers: audioData.markers || [],
        commentsCount: (audioData.comments || []).length,
        markersCount: (audioData.markers || []).length,
        regionsCount: (audioData.regions || []).length
      });

      const audioDataRef = doc(db, 'users', userId, 'sessionHistory', sessionId, 'audioData', recordingId);
      console.log('RecordingService: Firestore path:', `users/${userId}/sessionHistory/${sessionId}/audioData/${recordingId}`);
      
      const dataToSave = {
        recordingId,
        practiceItemId,
        sessionId,
        userId,
        regions: audioData.regions || [],
        comments: audioData.comments || [],
        markers: audioData.markers || [],
        lastUpdated: audioData.lastUpdated || new Date()
      };

      await setDoc(audioDataRef, dataToSave);

      console.log('RecordingService: Audio data saved successfully to Firestore');
      return true;
    } catch (error) {
      console.error('RecordingService: Error saving audio data:', error);
      throw error;
    }
  }

  getBestMimeType() {
    const videoMimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm',
      'video/mp4'
    ];

    const audioMimeTypes = [
      'audio/webm;codecs=opus',     // Opus codec - excellent quality
      'audio/webm;codecs=pcm',      // Uncompressed when available
      'audio/mp4;codecs=mp4a.40.2', // AAC codec
      'audio/webm',                 // Fallback
      'audio/ogg;codecs=opus'       // Alternative fallback
    ];

    const candidates = this.mode === 'video' ? videoMimeTypes : audioMimeTypes;

    for (const mimeType of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType)) {
        console.log(`Using MIME type: ${mimeType}`);
        return mimeType;
      }
    }

    return this.mode === 'video' ? 'video/webm' : 'audio/webm';
  }

  // Create/update the per-user recording metadata doc. The transcode Cloud
  // Function listens at the same path and flips `status` to 'ready' once
  // HLS variants and the poster image have been produced.
  async writeMediaRecordingDoc(userId, recording, extras = {}) {
    try {
      if (!userId || !recording?.id) return null;
      const docRef = doc(db, mediaRecordingDocPath(userId, recording.id));
      const payload = {
        id: recording.id,
        userId,
        mediaType: recording.mediaType || 'audio',
        status: recording.status || 'processing',
        storagePath: recording.storagePath || recording.filename || null,
        audioUrl: recording.audioUrl || null,
        previewUrl: recording.previewUrl || recording.url || null,
        hlsPath: recording.hlsPath || null,
        posterPath: recording.posterPath || null,
        durationMs: Math.round((recording.duration || 0) * 1000) || null,
        sizeBytes: recording.size || null,
        ownerType: extras.ownerType || 'session',
        ownerId: extras.ownerId || recording.sessionId || null,
        practiceItemId: recording.practiceItemId || null,
        sessionId: recording.sessionId || null,
        viewerUids: Array.isArray(extras.viewerUids) ? extras.viewerUids : [],
        createdAt: recording.createdAt || new Date(),
        updatedAt: serverTimestamp()
      };
      // Drop null keys so Firestore doesn't store a bunch of empty fields.
      const cleaned = {};
      for (const [k, v] of Object.entries(payload)) {
        if (v !== null && v !== undefined) cleaned[k] = v;
      }
      await setDoc(docRef, cleaned, { merge: true });
      return mediaRecordingDocPath(userId, recording.id);
    } catch (error) {
      console.error('writeMediaRecordingDoc failed:', error);
      return null;
    }
  }

  subscribeMediaRecording(userId, recordingId, callback) {
    if (!userId || !recordingId) return () => {};
    const docRef = doc(db, mediaRecordingDocPath(userId, recordingId));
    return onSnapshot(docRef, (snap) => {
      if (!snap.exists()) return;
      callback(snap.data());
    }, (err) => {
      console.warn('subscribeMediaRecording error:', err);
    });
  }

  // NEW: Refresh download URL for expired/invalid recordings
  async refreshDownloadURL(originalUrl) {
    try {
      console.log('🔍 Attempting to refresh URL:', originalUrl);
      
      // Extract the file path from the original Firebase Storage URL
      const urlMatch = originalUrl.match(/\/o\/(.+?)\?alt=media/);
      if (!urlMatch) {
        throw new Error('Invalid Firebase Storage URL format');
      }
      
      // Decode the URL-encoded path
      const encodedPath = urlMatch[1];
      const decodedPath = decodeURIComponent(encodedPath);
      console.log('🔍 Extracted file path:', decodedPath);
      
      const storageRef = ref(storage, decodedPath);
      
      // Generate a fresh download URL
      const downloadURL = await getDownloadURL(storageRef);
      console.log('✅ Successfully refreshed download URL:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Error refreshing download URL:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        originalUrl: originalUrl
      });
      throw error;
    }
  }
}
